// Minimal RFC-4180-ish CSV parser.
//
// Supports:
//   - Quoted fields: "value with, comma"
//   - Escaped quotes: "value with ""quote"""  ->  value with "quote"
//   - Newlines inside quoted fields
//   - CRLF and LF line endings
//   - Trailing newline (ignored)
//
// Delimiter: comma, semicolon or tab, detected from the header line.
// This is not politeness — Excel on an Indonesian Windows uses ";" as its list
// separator. It opens our comma file with every column stacked in column A, and
// when the operator fixes prices and saves, it writes semicolons back. A
// comma-only parser rejects that file wholesale, which is exactly the
// "export, edit, import" loop this feature exists to serve.
//
// Does NOT support:
//   - Comments (Excel-style)
//
// Returns:
//   - headers: trimmed, lowercased column names (for case-insensitive matching)
//   - rows: array of objects keyed by header name
//   - parseErrors: errors from the parser itself (e.g. unmatched quote)

export interface CsvParseResult {
  headers: string[];
  rows: Record<string, string>[];
  parseErrors: string[];
}

/**
 * Which character separates fields. Counted on the header line only, outside
 * quotes; the winner is whichever appears most. Ties go to the comma.
 */
function detectDelimiter(headerLine: string): string {
  const counts = [',', ';', '\t'].map((d) => {
    let n = 0;
    let inQ = false;
    for (const c of headerLine) {
      if (c === '"') inQ = !inQ;
      else if (c === d && !inQ) n += 1;
    }
    return { d, n };
  });
  counts.sort((a, b) => b.n - a.n);
  return counts[0].n > 0 ? counts[0].d : ',';
}

export function parseCsv(text: string): CsvParseResult {
  // Strip a leading UTF-8 BOM. downloadCsv() *writes* one so Excel opens the
  // file in the right encoding, so without this the app cannot read back a file
  // it exported itself: the first header arrives as "\uFEFFsku", the sku column
  // is never found, and every row fails for a reason that is invisible on
  // screen. Excel and Google Sheets both emit it on save too.
  if (text.charCodeAt(0) === 0xfeff) text = text.slice(1);

  // "sep=;" on the first line is Excel's own way of declaring the delimiter,
  // and it writes one when saving CSV in some locales. Honour it and drop it —
  // left in place it would be read as the header row.
  const sepMatch = /^sep=(.)\r?\n/i.exec(text);
  let delimiter: string;
  if (sepMatch) {
    delimiter = sepMatch[1];
    text = text.slice(sepMatch[0].length);
  } else {
    const firstLineEnd = text.search(/\r?\n/);
    delimiter = detectDelimiter(firstLineEnd === -1 ? text : text.slice(0, firstLineEnd));
  }

  const parseErrors: string[] = [];
  const allRows: string[][] = [];

  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  let lineNum = 1;

  const pushField = () => {
    row.push(field);
    field = '';
  };
  const pushRow = () => {
    pushField();
    // Skip purely empty trailing rows (single empty field).
    const isEmpty = row.length === 0 || (row.length === 1 && row[0] === '');
    if (!isEmpty) allRows.push(row);
    row = [];
  };

  for (let i = 0; i < text.length; i += 1) {
    const c = text[i];

    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          // Escaped quote inside a quoted field
          field += '"';
          i += 1;
        } else {
          // End of quoted field
          inQuotes = false;
        }
      } else {
        if (c === '\n') lineNum += 1;
        field += c;
      }
      continue;
    }

    if (c === '"') {
      inQuotes = true;
    } else if (c === delimiter) {
      pushField();
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i += 1;
      lineNum += 1;
      pushRow();
    } else {
      field += c;
    }
  }

  // Last field/row
  if (inQuotes) {
    parseErrors.push(`Baris ${lineNum}: tanda kutip dibuka tapi tidak ditutup`);
  }
  if (field !== '' || row.length > 0) {
    pushRow();
  }

  if (allRows.length === 0) {
    return { headers: [], rows: [], parseErrors: ['File CSV kosong'] };
  }

  const headers = allRows[0].map((h) => h.trim().toLowerCase());
  if (headers.length === 0 || (headers.length === 1 && headers[0] === '')) {
    parseErrors.push('Header CSV kosong atau tidak valid');
    return { headers: [], rows: [], parseErrors };
  }

  const dataRows = allRows.slice(1);
  const rows = dataRows.map((r, idx) => {
    const obj: Record<string, string> = {};
    headers.forEach((h, i) => {
      obj[h] = (r[i] ?? '').trim();
    });
    // Track the original row index (1-based, after header) for error reporting.
    obj.__row = String(idx + 2);
    return obj;
  });

  return { headers, rows, parseErrors };
}

/**
 * Stringify a single CSV field, quoting when it contains separator / quote / newline.
 */
export function csvField(
  value: string | number | boolean | undefined | null,
  delimiter = ',',
): string {
  if (value === undefined || value === null) return '';
  const s = String(value);
  if (s.includes(delimiter) || /["\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/** Build a CSV string from header + row arrays. */
export function buildCsv(
  headers: string[],
  rows: Array<Record<string, unknown>>,
  delimiter = ',',
): string {
  const cell = (v: unknown) => csvField(v as string | number | boolean | undefined, delimiter);
  const lines: string[] = [headers.map((h) => cell(h)).join(delimiter)];
  for (const row of rows) {
    lines.push(headers.map((h) => cell(row[h])).join(delimiter));
  }
  return lines.join('\n') + '\n';
}

/**
 * The same CSV, prefixed with Excel's `sep=` hint.
 *
 * Without it, Excel on a locale whose list separator is ";" (Indonesian
 * included) puts every column into column A and the file is unusable for the
 * edit-and-reimport loop. parseCsv reads the hint back and strips it, so a file
 * written this way still round-trips through this app.
 */
export function buildCsvForExcel(
  headers: string[],
  rows: Array<Record<string, unknown>>,
): string {
  return 'sep=,\n' + buildCsv(headers, rows);
}

/** Trigger a browser download for a Blob with the given filename. */
export function downloadCsv(filename: string, csv: string): void {
  // Prepend UTF-8 BOM so Excel reads non-ASCII correctly. The caller decides
  // whether to include a `sep=` hint (see buildCsvForExcel).
  const blob = new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
